import { DEFAULT_CATEGORY_SCHEME, crawlerCategory, crawlerCategoryLabel } from '~features/crawler-filter';
import type { DiscoverabilityPagesScope } from './api/discoverabilityPagesScope';
import type { AiCrawlerLineup } from '../shared';
import { dates } from '~library/dates';

const ALL_DOMAINS = 'All domains';
const ALL_CRAWLERS = 'All AI crawlers';

export type ExportScopeRow = {
  label: string;
  value: string;
};

// Local days, matching how the window was resolved from the picker and how the
// modal states it — the UTC date can name the previous day west of the line.
function windowLabel(scope: DiscoverabilityPagesScope): string {
  return `${dates.dayOfPointInTime(scope.dateTimeFrom)} – ${dates.dayOfPointInTime(scope.dateTimeTo)}`;
}

/**
 * Two or more domains can only reach the table through the status banner's
 * "Inspect pages" link, which pins the scope to the domains the daily scan
 * evaluated — the filter bar picks at most one. So a count is the honest
 * summary there (the list can run to dozens), while a single domain is worth
 * naming outright whichever path it arrived by.
 */
function domainsLabel(domains: readonly string[] | undefined): string {
  if (domains === undefined || domains.length === 0) return ALL_DOMAINS;
  if (domains.length === 1) return domains[0];
  return `${domains.length} domains from the latest scan`;
}

/**
 * Crawler ids are granular (`claude-web`, `anthropic-ai`, …) while the filter
 * and the KPIs speak in providers, so ids fold into their provider label and
 * repeat only once. A selection covering every tracked provider says the same
 * thing as no filter at all, and reads better.
 */
function crawlersLabel(lineup: AiCrawlerLineup, crawlers: readonly string[] | undefined): string {
  if (crawlers === undefined || crawlers.length === 0) return ALL_CRAWLERS;

  const categories = crawlers
    .map((id) => crawlerCategory(id, DEFAULT_CATEGORY_SCHEME))
    .filter((category, index, all) => all.indexOf(category) === index);
  if (categories.length >= lineup.categories.length) return ALL_CRAWLERS;

  return categories.map((category) => crawlerCategoryLabel(category)).join(', ');
}

/**
 * The page-level filters the export inherits, as label/value lines for the
 * export modal. Deliberately only the three the filter bar owns — the table's
 * own URL search, status filter and sort apply too, and the modal says so in
 * prose rather than listing them.
 */
export function exportScopeSummary(
  lineup: AiCrawlerLineup,
  scope: DiscoverabilityPagesScope
): ExportScopeRow[] {
  return [
    { label: 'Dates', value: windowLabel(scope) },
    { label: 'Domains', value: domainsLabel(scope.domains) },
    { label: 'Crawlers', value: crawlersLabel(lineup, scope.crawlers) },
  ];
}
